import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, Validators, ReactiveFormsModule } from '@angular/forms';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';

import { MatTableDataSource } from '@angular/material/table';
import { AgrFormModule } from '@next/core-lib/form';
import { LocaleService } from '@next/core-lib/i18n';
import { AgrSharedModule } from '@next/core-lib/shared';
import { TableColumnTypes, TableConfig, AgrTableModule, TableColumn } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { RationComponent, SiteState } from '../../../shared/models/models';

import { getRationComponents } from '../../../store/selectors';
import { ComponentLoadingOrder } from '../../models/measurements.model';

interface MeasurementLoadingOrderRow {
  id: number;
  name: string;
  order: number;
}

@Component({
  selector: 'app-measurement-loading-order',
  templateUrl: './measurement-loading-order.component.html',
  styleUrls: ['./measurement-loading-order.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCardModule,
    MatInputModule,
    AgrFormModule,
    AgrTableModule,
    AgrSharedModule,
  ],
})
export class MeasurementLoadingOrderComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly localeService = inject(LocaleService);

  @ViewChild('nameColumn') nameColumn: TemplateRef<unknown>;

  @Input()
  loadingOrder: ComponentLoadingOrder[];
  @Input()
  mixingTime: number;

  @Output()
  loadingOrderChange: EventEmitter<ComponentLoadingOrder[]> = new EventEmitter();
  @Output()
  mixingTimeChange: EventEmitter<number> = new EventEmitter();
  @Output()
  mixingTimeIsValidChange: EventEmitter<boolean> = new EventEmitter();

  tableConfig: TableConfig = {
    columns: [],
    dataSource: new MatTableDataSource([]),
    header: false,
    footer: false,
    scrolling: false,
    alternateRows: true,
    dragDrop: true,
    dragDropped: (event) => this.dragDroppedLoadingOrderComponent(event),
    selectColumn: false,
    pagination: false,
    disableCards: true,
    actionColumn: true,
    actions: [
      {
        id: 'delete',
        label: this.localeService.translate('measurements.wizard.loading-order.delete'),
        icon: 'delete_outline',
        callback: this.deleteLoadingOrderComponent.bind(this),
        primary: true,
        disabled: () => false,
      },
    ],
  };

  rationComponentFormControl: FormControl;
  mixingTimeFormControl: FormControl;

  rationComponents$ = this.store.select(getRationComponents);
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.rationComponentFormControl = new FormControl();
    this.mixingTimeFormControl = new FormControl(this.mixingTime, [
      Validators.min(0),
      Validators.max(999),
    ]);

    this.mixingTimeFormControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((newMixingTime: number) => {
        this.mixingTimeChange.emit(newMixingTime);
        this.mixingTimeIsValidChange.emit(this.mixingTimeFormControl.valid);
      });

    this.mixingTimeIsValidChange.emit(this.mixingTimeFormControl.valid);
  }

  ngAfterViewInit(): void {
    this.tableConfig = {
      ...this.tableConfig,
      columns: this.createColumns(),
    };
  }

  ngOnChanges({ loadingOrder }: SimpleChanges): void {
    if (
      !loadingOrder?.currentValue ||
      JSON.stringify(loadingOrder?.previousValue) === JSON.stringify(loadingOrder?.currentValue)
    ) {
      return;
    }

    this.tableConfig = {
      ...this.tableConfig,
      dataSource: new MatTableDataSource(this.createDataSource(this.loadingOrder)),
    };
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addRationComponent(event: MatSelectChange): void {
    const newLoadingOrder = [
      ...(this.loadingOrder || []),
      {
        component: event.value as RationComponent,
        order: this.loadingOrder?.length ? this.loadingOrder.length : 0,
      },
    ];

    this.rationComponentFormControl.setValue(null, { emitEvent: false });
    this.loadingOrderChange.emit(newLoadingOrder);
  }

  private createDataSource(loadingOrder: ComponentLoadingOrder[]): MeasurementLoadingOrderRow[] {
    return (loadingOrder || [])
      .slice()
      .sort((row1, row2) => row1.order - row2.order)
      .map((loadingOrderItem) => ({
        id: loadingOrderItem.component.id,
        name: loadingOrderItem.component.name,
        order: loadingOrderItem.order,
      }));
  }

  private createColumns(): TableColumn[] {
    return [
      {
        id: 'name',
        label: 'measurements.wizard.loading-order.component',
        type: TableColumnTypes.TEMPLATE,
        template: this.nameColumn,
      },
    ];
  }

  private dragDroppedLoadingOrderComponent(event: CdkDragDrop<unknown>) {
    let newLoadingOrder = this.loadingOrder.slice();
    moveItemInArray(newLoadingOrder, event.previousIndex, event.currentIndex);
    newLoadingOrder = newLoadingOrder.map((loadingOrderItem, index) => ({
      ...loadingOrderItem,
      order: index,
    }));

    this.loadingOrderChange.emit(newLoadingOrder);
  }

  private deleteLoadingOrderComponent(row: MeasurementLoadingOrderRow): void {
    let newLoadingOrder = this.loadingOrder.slice();
    newLoadingOrder.splice(row.order, 1);
    newLoadingOrder = newLoadingOrder.map((loadingOrderItem, index) => ({
      ...loadingOrderItem,
      order: index,
    }));

    this.loadingOrderChange.emit(newLoadingOrder);
  }
}
